import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Container, Row as UnstyledRow } from 'styled-bootstrap-grid';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { usePlayer, Track, Filter, style } from '../api';
import { GenreIdentifier } from '../api/lib/api/genres';
import { PlaylistType } from '../api/redux/dispatch/player';

import TrackLineUnstyled from './common/TrackLine';
import { BigBorderBtn } from './common/Buttons';
import { NoData } from './common/Text';
import { CenteredLoader } from './common/Loader';
import SharingModal from './Modals/SharingModal';
import { noDesktop } from '../style/constants';

export interface StateProps {
  tracks: Track[];
  isFetching: boolean;
  isProfilePage?: boolean;
  currentPage?: number;
  lastPageReached: boolean;
  playlistType: PlaylistType;
}

export interface DispatchProps {
  fetchTracks?: (filters: Filter, virppPicks?: boolean) => void;
}

export type OwnProps = {
  id?: number;
  genre?: GenreIdentifier;
  includeNumbers?: boolean;
  isCurrentUserProfilePage?: boolean;
};

type Props = StateProps & DispatchProps & OwnProps;

const TracksList = ({
  fetchTracks,
  id,
  currentPage,
  tracks,
  isFetching,
  isProfilePage,
  isCurrentUserProfilePage,
  includeNumbers,
  lastPageReached,
  playlistType,
}: Props) => {
  const { t } = useTranslation('common');
  const [sharingTrackId, setShowSharingTrackId] = useState<number>();

  const toggleSharingModal = (trackId: number) =>
    setShowSharingTrackId(trackId);

  const { query } = useRouter();
  const { playTrackByIdOrEnqueuePlaylist } = usePlayer();

  const playTrack = playTrackByIdOrEnqueuePlaylist(
    tracks.map((s) => s.id),
    playlistType,
  );

  /**
   * TODO - This needs to be sorted out, like for real,
   * Need to ask for more intel on this magic
   */
  useEffect(() => {
    if (!fetchTracks) return;
    if (Object.keys(query).filter((key) => key !== 'profileId').length > 0) {
      // make sure page is not added to filters
      delete query.page;
      if (query.genre === 'top10VirppPicks') {
        fetchTracks({}, true);
      } else {
        fetchTracks(query);
      }
    } else if (id !== undefined) {
      fetchTracks({ id });
    } else {
      fetchTracks({});
    }
  }, [id, query, fetchTracks]);

  const loadMoreTracks = () => {
    // for charts and explore
    if (id) {
      query.id = id.toString();
    }

    query.page = ((currentPage || 1) + 1).toString();
    if (fetchTracks) {
      fetchTracks(query);
    }
  };

  const showBtnOnFirstLoad = tracks.length > 4;
  const empty = !isFetching && tracks.length === 0;
  const showLoadMoreButton =
    (!lastPageReached || showBtnOnFirstLoad) && !isFetching;

  if (empty) {
    return (
      <OuterContainer>
        <NoData>{t('common:noTracksFound') as string}</NoData>
      </OuterContainer>
    );
  }

  return (
    <OuterContainer>
      <Container>
        {tracks.map((track, index) => (
          <Row key={track.id}>
            <TrackLine
              track={track}
              genre={track.genre}
              chartNumber={includeNumbers ? index + 1 : undefined}
              playTrack={playTrack}
              openShareModal={toggleSharingModal}
              isProfilePage={isProfilePage}
              isCurrentUserProfilePage={isCurrentUserProfilePage}
            />
          </Row>
        ))}
      </Container>
      {showLoadMoreButton && (
        <BigBorderBtn
          onClick={loadMoreTracks}
          style={{
            marginBottom: 20,
            fontSize: 14,
            letterSpacing: 0,
          }}
        >
          {t('common:loadMore') as string}
        </BigBorderBtn>
      )}
      {isFetching && (
        <CenteredLoader style={{ paddingTop: 20, marginBottom: 20 }} />
      )}

      <SharingModal
        show={Boolean(sharingTrackId)}
        close={() => {
          setShowSharingTrackId(undefined);
        }}
        type="tracks"
        id={sharingTrackId}
      />
    </OuterContainer>
  );
};

export default TracksList;

const Row = styled(UnstyledRow)`
  border-bottom: 1px solid ${style.grey_light_2_new};
  padding: 1.5rem;
  transition: 0.2s;

  :hover {
    box-shadow: 2px 3px 13px -5px rgb(0 0 0 / 27%);
  }

  @media ${noDesktop} {
    padding: 10px 10px;
  }
`;

const TrackLine = styled(TrackLineUnstyled)`
  & {
    margin-bottom: 0px;
  }
`;

const OuterContainer = styled.div`
  display: flex;
  flex-direction: column;
`;
